/**
 * Protobuf decoder for Biostream messages over the Meshtastic BLE transport.
 *
 * Inbound wire format (board → phone):
 *   FromRadio { packet (2): MeshPacket { decoded (4): Data { portnum (1): 256, payload (2): BiostreamPayload } } }
 *
 * decodeBiostreamPayload() unwraps the Meshtastic envelope automatically.
 *
 * BiostreamPayload fields:
 *   1 (string)  watch_id
 *   2 (uint64)  timestamp_ms
 *   3 (msg)     pair_request      { firmware_version (1): string }
 *   4 (msg)     pair_confirm
 *   5 (msg)     pair_ack
 *   6 (msg)     unpair
 *   7 (msg)     unpair_ack
 *   8 (msg)     triage_alert
 *   9 (msg)     man_down
 *  10 (msg)     status_poll
 *  11 (msg)     status_response
 *  12 (msg)     discover_request
 *  13 (msg)     discover_response { firmware_version (1): string }
 *  14 (msg)     medic_invite      { field_event_id (1), host_name (2), host_user_id (3) }
 *  15 (msg)     watch_event_relay { type (1), soldier_id (2), soldier_name (3), triage_level (4),
 *                                   vitals (5), battery_pct (6), confidence (7),
 *                                   latitude_i (8), longitude_i (9) }
 *
 * TriageAlert fields:
 *   1 (enum)            triage_level
 *   2 (float)           triage_score
 *   3 (uint32)          confidence
 *   4 (repeated enum)   diagnoses   — packed varint in proto3
 *   5 (msg)             vitals (meshtastic.HealthMetrics)
 *   6 (uint32)          battery_pct
 *   7 (sfixed32)        latitude_i   — degrees × 1e7
 *   8 (sfixed32)        longitude_i
 *   9 (uint32)          gps_accuracy
 *
 * StatusResponse fields:
 *   1 (msg)    vitals
 *   2 (enum)   activity_state
 *   3 (uint32) battery_pct
 *   4 (sfixed32) latitude_i
 *   5 (sfixed32) longitude_i
 *   6 (uint32)   gps_accuracy
 *
 * meshtastic.HealthMetrics fields:
 *   1 (uint32)  heart_bpm   (varint)
 *   2 (uint32)  spO2        (varint)
 *   3 (float)   temperature (fixed32)
 */

export type BiostreamMessageType =
  | "pair_request"
  | "pair_confirm"
  | "pair_ack"
  | "unpair"
  | "unpair_ack"
  | "triage_alert"
  | "man_down"
  | "status_poll"
  | "status_response"
  | "discover_request"
  | "discover_response"
  | "medic_invite"
  | "watch_relay"
  | "unknown";

export interface DecodedBiostreamPayload {
  watchId: string;
  timestampMs: number;
  type: BiostreamMessageType;
  // triage_alert / man_down / watch_relay
  vitals?: { hr?: number; spo2?: number; tempC?: number };
  triageLevel?: number;
  triageScore?: number;
  confidence?: number;
  diagnoses?: number[];
  latitudeI?: number;
  longitudeI?: number;
  gpsAccuracy?: number;
  batteryPct?: number;
  // status_response
  activityState?: number;
  // pair_request / discover_response
  firmwareVersion?: string;
  // medic_invite
  fieldEventId?: string;
  hostName?: string;
  hostUserId?: string;
  // watch_event_relay
  relayType?: string;
  relaySoldierId?: string;
  relaySoldierName?: string;
}

const FIELD_TO_TYPE: Record<number, BiostreamMessageType> = {
  3:  "pair_request",
  4:  "pair_confirm",
  5:  "pair_ack",
  6:  "unpair",
  7:  "unpair_ack",
  8:  "triage_alert",
  9:  "man_down",
  10: "status_poll",
  11: "status_response",
  12: "discover_request",
  13: "discover_response",
  14: "medic_invite",
  15: "watch_relay",
};

// ─── Reading primitives ──────────────────────────────────────────────────────

function readVarint(buf: Uint8Array, pos: number): [bigint, number] {
  let result = 0n;
  let shift = 0n;
  while (pos < buf.length) {
    const byte = buf[pos++];
    result |= BigInt(byte & 0x7f) << shift;
    if ((byte & 0x80) === 0) break;
    shift += 7n;
  }
  return [result, pos];
}

function readString(buf: Uint8Array, pos: number, len: number): string {
  return new TextDecoder().decode(buf.subarray(pos, pos + len));
}

function readFloat(buf: Uint8Array, pos: number): number {
  return new DataView(buf.buffer, buf.byteOffset + pos, 4).getFloat32(0, true);
}

function readFixed32(buf: Uint8Array, pos: number): number {
  return (buf[pos] | (buf[pos+1] << 8) | (buf[pos+2] << 16) | (buf[pos+3] << 24)) >>> 0;
}

function skipField(buf: Uint8Array, pos: number, wireType: number): number {
  if (wireType === 0) {
    while (pos < buf.length && (buf[pos++] & 0x80) !== 0) { /* skip varint bytes */ }
  } else if (wireType === 1) {
    pos += 8;
  } else if (wireType === 2) {
    const [len, p] = readVarint(buf, pos);
    pos = p + Number(len);
  } else if (wireType === 5) {
    pos += 4;
  }
  return pos;
}

// ─── Meshtastic envelope unwrapping ─────────────────────────────────────────
//
// FromRadio { packet (2): MeshPacket { decoded (4): Data { portnum (1), payload (2) } } }

function unwrapFromRadio(buf: Uint8Array): Uint8Array | null {
  let pos = 0;
  while (pos < buf.length) {
    const [tag, p] = readVarint(buf, pos); pos = p;
    if (tag === 0n) break;
    const fieldNum = Number(tag >> 3n);
    const wireType = Number(tag & 7n);
    if (wireType === 2) {
      const [len, p2] = readVarint(buf, pos); pos = p2;
      const msgLen = Number(len);
      if (fieldNum === 2) return unwrapMeshPacket(buf.subarray(pos, pos + msgLen));
      pos += msgLen;
    } else {
      pos = skipField(buf, pos, wireType);
    }
  }
  return null;
}

function unwrapMeshPacket(buf: Uint8Array): Uint8Array | null {
  let pos = 0;
  while (pos < buf.length) {
    const [tag, p] = readVarint(buf, pos); pos = p;
    if (tag === 0n) break;
    const fieldNum = Number(tag >> 3n);
    const wireType = Number(tag & 7n);
    if (wireType === 2) {
      const [len, p2] = readVarint(buf, pos); pos = p2;
      const msgLen = Number(len);
      if (fieldNum === 4) return unwrapData(buf.subarray(pos, pos + msgLen)); // decoded=Data
      pos += msgLen;
    } else if (wireType === 5) {
      pos += 4; // skip fixed32 fields (from, to, id, etc.)
    } else {
      pos = skipField(buf, pos, wireType);
    }
  }
  return null;
}

function unwrapData(buf: Uint8Array): Uint8Array | null {
  let pos = 0;
  let portnum = 0;
  let payloadSlice: Uint8Array | null = null;
  while (pos < buf.length) {
    const [tag, p] = readVarint(buf, pos); pos = p;
    if (tag === 0n) break;
    const fieldNum = Number(tag >> 3n);
    const wireType = Number(tag & 7n);
    if (wireType === 0) {
      const [val, p2] = readVarint(buf, pos); pos = p2;
      if (fieldNum === 1) portnum = Number(val);
    } else if (wireType === 2) {
      const [len, p2] = readVarint(buf, pos); pos = p2;
      const msgLen = Number(len);
      if (fieldNum === 2) payloadSlice = buf.subarray(pos, pos + msgLen);
      pos += msgLen;
    } else {
      pos = skipField(buf, pos, wireType);
    }
  }
  return portnum === 256 && payloadSlice ? payloadSlice : null;
}

// ─── HealthMetrics sub-message decoder ──────────────────────────────────────

function decodeHealthMetrics(buf: Uint8Array): { hr?: number; spo2?: number; tempC?: number } {
  let pos = 0;
  const result: { hr?: number; spo2?: number; tempC?: number } = {};
  while (pos < buf.length) {
    const [tag, p] = readVarint(buf, pos); pos = p;
    const fieldNum = Number(tag >> 3n);
    const wireType = Number(tag & 7n);
    if (wireType === 0) {
      const [val, p2] = readVarint(buf, pos); pos = p2;
      if (fieldNum === 1) result.hr = Number(val);
      else if (fieldNum === 2) result.spo2 = Number(val);
    } else if (wireType === 5) {
      if (fieldNum === 3) result.tempC = readFloat(buf, pos);
      pos += 4;
    } else {
      pos = skipField(buf, pos, wireType);
    }
  }
  return result;
}

// ─── TriageAlert sub-message decoder ────────────────────────────────────────

function decodeTriageAlert(buf: Uint8Array, out: DecodedBiostreamPayload): void {
  let pos = 0;
  while (pos < buf.length) {
    const [tag, p] = readVarint(buf, pos); pos = p;
    const fieldNum = Number(tag >> 3n);
    const wireType = Number(tag & 7n);

    if (wireType === 0) {
      const [val, p2] = readVarint(buf, pos); pos = p2;
      if (fieldNum === 1) out.triageLevel = Number(val);
      else if (fieldNum === 3) out.confidence = Number(val);
      else if (fieldNum === 4) {
        // Unpacked repeated enum (fallback — proto3 normally packs scalars)
        if (!out.diagnoses) out.diagnoses = [];
        out.diagnoses.push(Number(val));
      }
      else if (fieldNum === 6) out.batteryPct = Number(val);
    } else if (wireType === 2) {
      const [len, p2] = readVarint(buf, pos); pos = p2;
      const msgLen = Number(len);
      if (fieldNum === 4) {
        // Packed repeated varint (proto3 default for scalar repeated fields)
        if (!out.diagnoses) out.diagnoses = [];
        const sub = buf.subarray(pos, pos + msgLen);
        let sp = 0;
        while (sp < sub.length) {
          const [v, np] = readVarint(sub, sp); sp = np;
          out.diagnoses.push(Number(v));
        }
      } else if (fieldNum === 5) {
        out.vitals = decodeHealthMetrics(buf.subarray(pos, pos + msgLen));
      }
      pos += msgLen;
    } else if (wireType === 5) {
      const raw = readFixed32(buf, pos);
      if (fieldNum === 2) out.triageScore = readFloat(buf, pos);  // float triage_score
      else if (fieldNum === 7) out.latitudeI  = raw | 0;          // sfixed32
      else if (fieldNum === 8) out.longitudeI = raw | 0;
      else if (fieldNum === 9) out.gpsAccuracy = raw;             // uint32
      pos += 4;
    } else {
      pos = skipField(buf, pos, wireType);
    }
  }
}

// ─── ManDown sub-message decoder ────────────────────────────────────────────

function decodeManDown(buf: Uint8Array, out: DecodedBiostreamPayload): void {
  let pos = 0;
  while (pos < buf.length) {
    const [tag, p] = readVarint(buf, pos); pos = p;
    const fieldNum = Number(tag >> 3n);
    const wireType = Number(tag & 7n);
    if (wireType === 0) {
      const [val, p2] = readVarint(buf, pos); pos = p2;
      if (fieldNum === 1) out.confidence = Number(val);
    } else if (wireType === 5) {
      const raw = readFixed32(buf, pos);
      if (fieldNum === 2) out.latitudeI  = raw | 0;
      else if (fieldNum === 3) out.longitudeI = raw | 0;
      else if (fieldNum === 4) out.gpsAccuracy = raw;
      pos += 4;
    } else {
      pos = skipField(buf, pos, wireType);
    }
  }
}

// ─── StatusResponse sub-message decoder ─────────────────────────────────────
// Fields: vitals (1, msg), activity_state (2, varint), battery_pct (3, varint),
//         latitude_i (4, sfixed32), longitude_i (5, sfixed32), gps_accuracy (6, uint32)

function decodeStatusResponse(buf: Uint8Array, out: DecodedBiostreamPayload): void {
  let pos = 0;
  while (pos < buf.length) {
    const [tag, p] = readVarint(buf, pos); pos = p;
    const fieldNum = Number(tag >> 3n);
    const wireType = Number(tag & 7n);
    if (wireType === 2) {
      const [len, p2] = readVarint(buf, pos); pos = p2;
      const msgLen = Number(len);
      if (fieldNum === 1) out.vitals = decodeHealthMetrics(buf.subarray(pos, pos + msgLen));
      pos += msgLen;
    } else if (wireType === 0) {
      const [val, p2] = readVarint(buf, pos); pos = p2;
      if (fieldNum === 2) out.activityState = Number(val);
      else if (fieldNum === 3) out.batteryPct = Number(val);
    } else if (wireType === 5) {
      const raw = readFixed32(buf, pos);
      if (fieldNum === 4) out.latitudeI  = raw | 0;
      else if (fieldNum === 5) out.longitudeI = raw | 0;
      else if (fieldNum === 6) out.gpsAccuracy = raw;
      pos += 4;
    } else {
      pos = skipField(buf, pos, wireType);
    }
  }
}

// ─── PairRequest / DiscoverResponse sub-message decoders ────────────────────

function decodeFirmwareVersion(buf: Uint8Array, out: DecodedBiostreamPayload): void {
  let pos = 0;
  while (pos < buf.length) {
    const [tag, p] = readVarint(buf, pos); pos = p;
    const fieldNum = Number(tag >> 3n);
    const wireType = Number(tag & 7n);
    if (wireType === 2) {
      const [len, p2] = readVarint(buf, pos); pos = p2;
      const slen = Number(len);
      if (fieldNum === 1) out.firmwareVersion = readString(buf, pos, slen);
      pos += slen;
    } else {
      pos = skipField(buf, pos, wireType);
    }
  }
}

// ─── MedicInvite sub-message decoder ────────────────────────────────────────
// Fields: field_event_id (1), host_name (2), host_user_id (3)

function decodeMedicInvite(buf: Uint8Array, out: DecodedBiostreamPayload): void {
  let pos = 0;
  while (pos < buf.length) {
    const [tag, p] = readVarint(buf, pos); pos = p;
    const fieldNum = Number(tag >> 3n);
    const wireType = Number(tag & 7n);
    if (wireType === 2) {
      const [len, p2] = readVarint(buf, pos); pos = p2;
      const slen = Number(len);
      if (fieldNum === 1) out.fieldEventId = readString(buf, pos, slen);
      else if (fieldNum === 2) out.hostName = readString(buf, pos, slen);
      else if (fieldNum === 3) out.hostUserId = readString(buf, pos, slen);
      pos += slen;
    } else {
      pos = skipField(buf, pos, wireType);
    }
  }
}

// ─── WatchEventRelay sub-message decoder ────────────────────────────────────
// Fields: type (1), soldier_id (2), soldier_name (3), triage_level (4),
//         vitals (5), battery_pct (6), confidence (7), latitude_i (8), longitude_i (9)

function decodeWatchEventRelay(buf: Uint8Array, out: DecodedBiostreamPayload): void {
  let pos = 0;
  while (pos < buf.length) {
    const [tag, p] = readVarint(buf, pos); pos = p;
    const fieldNum = Number(tag >> 3n);
    const wireType = Number(tag & 7n);
    if (wireType === 2) {
      const [len, p2] = readVarint(buf, pos); pos = p2;
      const msgLen = Number(len);
      if (fieldNum === 1) out.relayType = readString(buf, pos, msgLen);
      else if (fieldNum === 2) out.relaySoldierId = readString(buf, pos, msgLen);
      else if (fieldNum === 3) out.relaySoldierName = readString(buf, pos, msgLen);
      else if (fieldNum === 5) out.vitals = decodeHealthMetrics(buf.subarray(pos, pos + msgLen));
      pos += msgLen;
    } else if (wireType === 0) {
      const [val, p2] = readVarint(buf, pos); pos = p2;
      if (fieldNum === 4) out.triageLevel = Number(val);
      else if (fieldNum === 6) out.batteryPct = Number(val);
      else if (fieldNum === 7) out.confidence = Number(val);
    } else if (wireType === 5) {
      const raw = readFixed32(buf, pos);
      if (fieldNum === 8) out.latitudeI  = raw | 0;
      else if (fieldNum === 9) out.longitudeI = raw | 0;
      pos += 4;
    } else {
      pos = skipField(buf, pos, wireType);
    }
  }
}

// ─── Top-level BiostreamPayload decoder ─────────────────────────────────────

function decodeBiostreamPayloadBuf(buf: Uint8Array): DecodedBiostreamPayload {
  let pos = 0;
  const out: DecodedBiostreamPayload = { watchId: "", timestampMs: 0, type: "unknown" };

  while (pos < buf.length) {
    const [tag, p] = readVarint(buf, pos); pos = p;
    if (tag === 0n) break;
    const fieldNum = Number(tag >> 3n);
    const wireType = Number(tag & 7n);

    if (wireType === 2) {
      const [len, p2] = readVarint(buf, pos); pos = p2;
      const msgLen = Number(len);
      if (fieldNum === 1) {
        out.watchId = readString(buf, pos, msgLen);
      } else if (fieldNum in FIELD_TO_TYPE) {
        out.type = FIELD_TO_TYPE[fieldNum];
        const sub = buf.subarray(pos, pos + msgLen);
        if (fieldNum === 8)  decodeTriageAlert(sub, out);
        else if (fieldNum === 9)  decodeManDown(sub, out);
        else if (fieldNum === 11) decodeStatusResponse(sub, out);
        else if (fieldNum === 3 || fieldNum === 13) decodeFirmwareVersion(sub, out);
        else if (fieldNum === 14) decodeMedicInvite(sub, out);
        else if (fieldNum === 15) decodeWatchEventRelay(sub, out);
      }
      pos += msgLen;
    } else if (wireType === 0) {
      const [val, p2] = readVarint(buf, pos); pos = p2;
      if (fieldNum === 2) out.timestampMs = Number(val);
    } else {
      pos = skipField(buf, pos, wireType);
    }
  }
  return out;
}

// ─── Public entry point ──────────────────────────────────────────────────────

/**
 * Decode a hex-encoded message from the board.
 * Automatically unwraps the Meshtastic FromRadio envelope if present.
 * Returns null if the bytes are empty or cannot be parsed at all.
 */
export function decodeBiostreamPayload(hex: string): DecodedBiostreamPayload | null {
  try {
    if (!hex || hex.length === 0) return null;
    const bytes = hexToBytes(hex);
    const buf = new Uint8Array(bytes);
    const biostreamBuf = unwrapFromRadio(buf) ?? buf;
    return decodeBiostreamPayloadBuf(biostreamBuf);
  } catch {
    return null;
  }
}

function hexToBytes(hex: string): number[] {
  const bytes: number[] = [];
  for (let i = 0; i < hex.length; i += 2) {
    bytes.push(parseInt(hex.slice(i, i + 2), 16));
  }
  return bytes;
}
